/**
 * RR-F1 BL-1b (D-803 C4), re-derived in BL-R2: where a shown stand-by ship holds at its station (plan
 * docs/plans/2026-10-11-rr-f1-parked-miners.md §3.6 BL-1b). Just outside both the station's mooring ring and the
 * hold orbit holding traffic flies (`R + holdOrbitM`, scale SC-5d3), so it never sits inside a big station's hull, on a
 * moored L's slot or in the ring that holding patrols and `holdSpot` contacts fly:
 *
 * - **Direction:** the moor ring's slot `rank mod slots` (`moor.ts moorSlotDirection`): the slots spread over the side
 *   away from the bay, none within `corridorClearDeg` of the bay's out axis, so the approach corridor stays clear.
 * - **Radius:** `max(moorRingRadius(R), R + holdOrbitM) + standbyClearM` (`standby.json`; `local.json holdOrbitM`):
 *   station-radius aware, as the ring and the orbit are (the 2.7 km Hub 5,100 m, the 1.2 km refinery 3,600 m, the 1 km
 *   scrapyard 3,400 m at holdOrbitM 2,000 and standbyClearM 400).
 *   A rank past the ring's slots (only when `slots` < `standbyVisiblePerStation`) goes one more `standbyClearM` out, so
 *   two ranks never share a point.
 *
 * A pure float64 function with an out parameter (world metres, D-011/D-013: the caller rebases before rendering);
 * it allocates nothing (D-014).
 */
import type { Vec3 } from '../core/math'
import { moorRingRadius, moorSlotDirection, type RingConfig } from '../docking/moor'

/**
 * The stand-by point of rank `rank` (0, 1, …: the ship's place among its station's shown stand-by ships) round a
 * station at `stationPos` with bounding radius `stationRadius` and bay out axis `bayOut`, `clearM` outside the larger of
 * the moor ring and the hold orbit (`stationRadius + holdOrbitM`). Into `out`, which is returned.
 */
export function standbyPoint(
  stationPos: Readonly<Vec3>,
  stationRadius: number,
  bayOut: Readonly<Vec3>,
  rank: number,
  moorCfg: RingConfig,
  holdOrbitM: number,
  clearM: number,
  out: Vec3,
): Vec3 {
  const k = rank > 0 ? rank : 0
  const layer = Math.floor(k / moorCfg.slots)
  const ring = moorRingRadius(stationRadius, moorCfg)
  const orbit = stationRadius + holdOrbitM
  const r = (ring > orbit ? ring : orbit) + clearM * (1 + layer)
  moorSlotDirection(bayOut, k, moorCfg, out)
  out.x = stationPos.x + r * out.x
  out.y = stationPos.y + r * out.y
  out.z = stationPos.z + r * out.z
  return out
}
